import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useResultantMembers } from 'hooks'
import { useDebounce } from 'packages/core'
import TableGrid from 'components/TableGrid'
import BooleanIcon from 'components/DynamicComponents/BooleanIcon'
import DateTimeGenericComponent from 'components/DynamicComponents/DateTimeGenericComponent'
import Search from 'components/Filters/Search'
import useSubcomponents from 'useSubcomponents'

const useTableStyles = makeStyles({
    headGrey: {
        backgroundColor: '#fbfbfd',
        height: '32px',
        boxShadow: ' 0 2px 8px 0 rgba(0, 0, 0, 0.05)',
    },
    table: {
        backgroundColor: '#ffffff',
        '& tr:hover': {
            backgroundColor: '#f7f8fa',
        },
    },
    tableRow: {
        border: ' solid 1px #efeff1',
        height: '80px',
    },
    tableCell: {
        overflowWrap: 'break-word',
        maxWidth: '220px',
        wordBreak: 'break-word',
        overflow: 'hidden',
        padding: '14px 16px',
        fontSize: '13px',
        border: 'none',
        borderBottom: '0',
    },
    tableHeaderCell: {
        border: 'none !important',
        padding: '16px',
        overflowWrap: 'break-word',
        wordBreak: 'normal',
        overflow: 'hidden',
        color: '#7d7c7c',
    },
})

export const ResultantMembers = ({ groupId }) => {
    const { t } = useTranslation()
    const tableClasses = useTableStyles()
    const { getAccessBasedAttributes } = useSubcomponents()
    const [sorting, setSorting] = useState({ sortOrder: null, sortBy: null })
    const [searchKey, setSearchKey] = useState('')

    const handleSort = (sorting) => {
        setSorting(sorting)
    }

    const handleSearchChange = (keyword) => {
        setSearchKey(keyword)
    }
    const handleClear = () => {
        setSearchKey('')
    }
    const debouncedSearchValue = useDebounce(searchKey)

    const queryData = {
        searchTerm: debouncedSearchValue,
        sorting,
    }

    const {
        list,
        loading: isLoading,
        pagination,
    } = useResultantMembers(groupId, queryData)

    const attributes = [
        {
            label: t('FriendlyName'),
            name: 'friendlyName',
            sortable: true,
            style: {
                width: '150px',
                color: '#282828',
                fontSize: '13px',
                fontWeight: 'bold',
            },
            //NOTE : Keeping the commented out code for subcomponent traceability
            // requireAccess: {
            //     control:
            //         'ResourceAdmin-Groups-Details-ResultantMembers-List-FriendlyName',
            // },
        },
        {
            label: t('Enabled'),
            name: 'disabled',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-Groups-Details-ResultantMembers-List-Enabled',
            },
            resolve: (item) => (
                <BooleanIcon data={{ item }} attribute={{ name: 'disabled' }} />
            ),
        },
        {
            label: t('Type'),
            name: 'userType',
            sortable: true,
            style: {
                color: '#282828',
                fontSize: '13px',
            },
            requireAccess: {
                control:
                    'ResourceAdmin-Groups-Details-ResultantMembers-List-Type',
            },
        },
        {
            name: 'lastLogonTime',
            label: t('LastLogin'),
            sortable: true,
            style: {
                color: '#282828',
                fontSize: '13px',
            },
            requireAccess: {
                control:
                    'ResourceAdmin-Groups-Details-ResultantMembers-List-LastLogin',
            },
            resolve: (item) => (
                <DateTimeGenericComponent
                    data={{ item }}
                    attribute={{ name: 'lastLogonTime' }}
                />
            ),
        },
        {
            name: 'logonName',
            label: t('LogonName'),
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-Groups-Details-ResultantMembers-List-LogonName',
            },
            style: {
                color: '#282828',
                fontSize: '13px',
            },
        },
        {
            name: 'domainOrDirectory',
            label: t('DomainOrDirectory'),
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-Groups-Details-ResultantMembers-List-DomainOrDirectory',
            },
            style: {
                color: '#282828',
                fontSize: '13px',
            },
        },
        {
            name: 'distinguishedName',
            label: t('DistinguishedName'),
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-Groups-Details-ResultantMembers-List-DistinguishedName',
            },
            style: {
                color: '#282828',
                fontSize: '13px',
                width: '350px',
            },
        },
    ]

    return (
        <>
            <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="end"
                paddingBottom="16px"
            >
                <Box width="308px" display="flex" alignItems="center">
                    <Search
                        handleSearch={handleSearchChange}
                        placeholder={t('SearchFilterMembers')}
                        value={searchKey}
                        handleClear={handleClear}
                        outlined
                    />
                </Box>
            </Box>

            <TableGrid
                loading={isLoading}
                data={list}
                attributes={getAccessBasedAttributes(attributes)}
                tableClasses={tableClasses}
                sorting={sorting}
                onSort={handleSort}
            />

            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '40px',
                }}
            >
                <Box>{pagination}</Box>
            </Box>
        </>
    )
}
